import { useLocation, useNavigate } from 'react-router-dom';

import classNames from 'classnames';

import { capitalize, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import CustomIcon from 'assets/Icon';
import Flex from 'modules/shared/components/general/Flex/Flex';

import { BrandStatusLabel, IBrand } from 'types/brands.types';

interface IBrandCardProps {
    brand: IBrand;
}

export default function BrandCard({ brand }: IBrandCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const onBrandClick = () => {
    navigate({ pathname: `${location.pathname}/${brand.id}` });
  };

  const expired = brand.email_status === BrandStatusLabel.EXPIRED;

  const disabled =
      brand.email_status === BrandStatusLabel.REGENERATING || brand.email_status === BrandStatusLabel.SENDING || brand.email_status === BrandStatusLabel.SENT || expired;

  const enabledWithStatus =
      brand.email_status === BrandStatusLabel.REGENERATED || brand.email_status === BrandStatusLabel.FAILED_TO_SEND || brand.email_status === BrandStatusLabel.FAILED_TO_REGENERATE;

  const sent = brand.email_status === BrandStatusLabel.SENT;

  // A cycle is due by the 1st of the month after it generated, so a quiet
  // pending card whose cycle month is no longer the current month is past
  // due. Failures and in-flight states keep their own, more severe badges.
  const quietPending =
      brand.email_status === BrandStatusLabel.CREATED || brand.email_status === BrandStatusLabel.REGENERATED;
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });
  const pastDue = quietPending && Boolean(brand.cycle) && brand.cycle !== currentMonth;

  const getTooltipTitle = () => {
    if (brand.email_status === BrandStatusLabel.REGENERATING) {
      return 'Regenerating...';
    }
    if (brand.email_status === BrandStatusLabel.SENDING) {
      return 'Sending...';
    }
    if (brand.email_status === BrandStatusLabel.REGENERATED) {
      return 'Regenerated.';
    }
    if (brand.email_status === BrandStatusLabel.FAILED_TO_REGENERATE) {
      return 'Failed to regenerate. Please try again.';
    }
    if (brand.email_status === BrandStatusLabel.FAILED_TO_SEND) {
      return 'Failed to send. Please try again.';
    }

    return '';
  };

  return (
    <div
      className={classNames('brand-wrapper')}
    >
      {((disabled && brand.email_status !== BrandStatusLabel.SENT && !expired) || enabledWithStatus) && !pastDue && (
        <Tooltip
          title={getTooltipTitle()}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#444444',
                fontSize: 18,
                fontWeight: 'normal',
              },
            },
            arrow: {
              sx: {
                color: '#444444',
              },
            },
          }}
          PopperProps={{
            sx: {
              backgroundColor: 'transparent',
            },
          }}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -4],
                  },
                },
              ],
            },
          }}>
          {brand.email_status === BrandStatusLabel.FAILED_TO_REGENERATE || brand.email_status === BrandStatusLabel.FAILED_TO_SEND ? (
            <CustomIcon name="warning_outlined" fontSize="small" sx={{ position: 'absolute', top: 15, right: 15, zIndex: 1000, color: "#B3261E" }} />
          ) : (
            <InfoOutlinedIcon fontSize="small" sx={{ position: 'absolute', top: 15, right: 15, zIndex: 1000, color: "#636363" }} />
          )}
        </Tooltip>
      )}
      {sent && (
        <Flex alignItems="center" gap="6px" style={{ position: 'absolute', top: 15, right: 15, zIndex: 1000 }}>
          <span className="sent-status-date">
            <strong>{capitalize(brand.email_status)}</strong> {brand.sent_at}
          </span>
          <CheckCircleIcon style={{ fontSize: 18, color: "#33850D" }} />
        </Flex>
      )}
      {expired && (
        <Flex alignItems="center" gap="6px" style={{ position: 'absolute', top: 15, right: 15, zIndex: 1000 }}>
          <span className="expired-status-date" data-testid="expired-badge">
            <strong>{brand.cycle}</strong> — expired, not sent
          </span>
          <CustomIcon name="warning_outlined" fontSize="small" sx={{ fontSize: 18, color: "#B3261E" }} />
        </Flex>
      )}
      {pastDue && (
        <Tooltip
          title="Past due"
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#444444',
                fontSize: 18,
                fontWeight: 'normal',
              },
            },
            arrow: {
              sx: {
                color: '#444444',
              },
            },
          }}
        >
          <span
            data-testid="past-due-badge"
            style={{ position: 'absolute', top: 15, right: 15, zIndex: 1000, width: 24, height: 24, display: 'inline-block' }}
          >
            <AccessTimeIcon sx={{ fontSize: 20, color: '#636363' }} />
            <WarningAmberIcon sx={{ fontSize: 14, color: '#F2A600', position: 'absolute', bottom: -2, right: -4 }} />
          </span>
        </Tooltip>
      )}
      <div
        className={classNames('brands', {
          disabled,
        })}
        onClick={disabled ? undefined : onBrandClick}
      >
        <img src={brand.logo_url} alt="Brand Logo"
          style={{ maxHeight: '120px', maxWidth: '120px', objectFit: 'cover' }}
        />
      </div>
    </div>
  );
}
